import React from 'react';
import {
    CheckSquare,
    Clock,
    AlertTriangle,
    CheckCircle,
    TrendingDown,
} from 'lucide-react';
import clsx from 'clsx';

interface ActionTrackerStatsProps {
    stats: {
        total: number;
        open: number;
        overdue: number;
        completed: number;
        riskReduction: number;
    };
    isDark: boolean;
}

const ActionTrackerStats: React.FC<ActionTrackerStatsProps> = ({ stats, isDark }) => {


    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className={clsx(
                'p-4 rounded-xl border',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                        <CheckSquare size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.total}</p>
                        <p className="text-sm text-slate-500">Total Actions</p>
                    </div>
                </div>
            </div>

            <div className={clsx(
                'p-4 rounded-xl border',
                isDark ? 'bg-amber-900/30 border-amber-800' : 'bg-amber-50 border-amber-200'
            )}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900">
                        <Clock size={20} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.open}</p>
                        <p className="text-sm text-amber-600 dark:text-amber-400">Open/In Progress</p>
                    </div>
                </div>
            </div>

            <div className={clsx(
                'p-4 rounded-xl border',
                stats.overdue > 0
                    ? (isDark ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200')
                    : (isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')
            )}>
                <div className="flex items-center gap-3">
                    <div className={clsx('p-2 rounded-lg', stats.overdue > 0 ? 'bg-red-100 dark:bg-red-900' : 'bg-slate-100 dark:bg-slate-700')}>
                        <AlertTriangle size={20} className={stats.overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500'} />
                    </div>
                    <div>
                        <p className={clsx('text-2xl font-bold', stats.overdue > 0 ? 'text-red-700 dark:text-red-300' : 'text-slate-800 dark:text-white')}>
                            {stats.overdue}
                        </p>
                        <p className={clsx('text-sm', stats.overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500')}>
                            Overdue
                        </p>
                    </div>
                </div>
            </div>

            <div className={clsx(
                'p-4 rounded-xl border',
                isDark ? 'bg-green-900/30 border-green-800' : 'bg-green-50 border-green-200'
            )}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                        <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
                        <p className="text-sm text-green-600 dark:text-green-400">Completed</p>
                    </div>
                </div>
            </div>

            <div className={clsx(
                'p-4 rounded-xl border',
                isDark ? 'bg-purple-900/30 border-purple-800' : 'bg-purple-50 border-purple-200'
            )}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                        <TrendingDown size={20} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.riskReduction}%</p>
                        <p className="text-sm text-purple-600 dark:text-purple-400">Risk Reduction</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActionTrackerStats;
